var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from "react";
import { InsuranceProducts, InsuranceType } from "../constants";
import { clickedBuyQuoteButton, impliedSawSavings } from "../lib/Avo";
import { convertCentsToDollars, shouldOpenInSameWindow } from "../lib/util";
import AppBody from "../components/AppBody";
import { Button } from "@savvyinsure/react-ui";
import { FooterContent } from "../components/SavvyResultsUI/SavvyResultsUI";
import Header from "../components/Header";
import { HeaderLogo } from "../components/RouteHeaderLogo";
import { SavvyIssuerIcon } from "../components/IssuerIcon";
import { formatDollars } from "../lib/formatCurrency";
import handleResultClick from "../utils/handleResultClick";
import homeIssuersPerState from "../features/search/homeIssuersPerState.json";
import issuersPerState from "../features/search/issuersPerState.json";
import { usaStates } from "typed-usa-states/dist/states";
import useAppSelector from "../hooks/useAppSelector";
import { useEffectOnceWhen } from "rooks";
import useEventPublisher from "../hooks/useEventPublisher";
import useFindIssuer from "../features/configuration/useFindIssuer";
import useImpressionType from "../services/savvy/useImpressionType";
import useSearchResults from "../services/savvy/useSearchResults";
var issuersByProduct = new Map([
    [InsuranceProducts.Auto, issuersPerState],
    [InsuranceProducts.Home, homeIssuersPerState],
]);
export default function AgnecyFullfilment() {
    var _a;
    var _b = useSearchResults().searchQuery.data, _c = _b === void 0 ? {} : _b, impressionId = _c.impressionId, agencyContent = _c.agencyContent, footerContent = _c.footerContent;
    var userState = useAppSelector(function (state) {
        var _a, _b, _c;
        var linkedPolicy = ((_a = state.policies.auto[0]) === null || _a === void 0 ? void 0 : _a.policyHolder) ||
            ((_b = state.policies.home[0]) === null || _b === void 0 ? void 0 : _b.policyHolder);
        var policyHolderState = ((_c = linkedPolicy === null || linkedPolicy === void 0 ? void 0 : linkedPolicy.address) === null || _c === void 0 ? void 0 : _c.state) ||
            state.unlinkedApplication.policyHolder.state;
        return usaStates.find(function (state) {
            var _a;
            return [state.abbreviation.toLowerCase(), state.name.toLowerCase()].includes((_a = policyHolderState.toLowerCase()) !== null && _a !== void 0 ? _a : "");
        });
    });
    var commonAnalyticsProperties = useAppSelector(function (state) { return state.analytics.commonAnalyticsProperties; });
    var eventPublisher = useEventPublisher();
    var findIssuer = useFindIssuer();
    var insuranceProduct = useAppSelector(function (state) { return state.applicant.insuranceProduct; });
    var impressionType = useImpressionType();
    var stateIssuersByProduct = insuranceProduct
        ? issuersByProduct.get(insuranceProduct)
        : issuersPerState;
    var stateIssuers = stateIssuersByProduct !== null && stateIssuersByProduct !== void 0 ? stateIssuersByProduct : issuersPerState;
    var stateData = userState
        ? stateIssuers.find(function (_a) {
            var state = _a.state;
            return state.toLowerCase() === userState.abbreviation.toLowerCase();
        })
        : undefined;
    var issuers = [
        stateData === null || stateData === void 0 ? void 0 : stateData.first_insurer,
        stateData === null || stateData === void 0 ? void 0 : stateData.second_insurer,
        stateData === null || stateData === void 0 ? void 0 : stateData.third_insurer,
    ]
        .filter(function (name) { return typeof name === "string"; })
        .map(function (issuerName) { return findIssuer(issuerName); })
        .filter(function (issuer) { return issuer !== undefined; });
    var annualSavings = ((_a = agencyContent === null || agencyContent === void 0 ? void 0 : agencyContent.estimatedMonthlySavingsCents) !== null && _a !== void 0 ? _a : 0) * 12;
    function handleClick() {
        var openInSameWindow = shouldOpenInSameWindow();
        clickedBuyQuoteButton(__assign(__assign({}, commonAnalyticsProperties), { impressionType: impressionType, 
            // the result list index is 1 based, and we pass 0-based indexes from arrays
            resultListPosition: 1, openInNewWindow: !openInSameWindow }));
        if (impressionId) {
            eventPublisher.clickOffer(agencyContent, impressionId);
        }
        if (agencyContent === null || agencyContent === void 0 ? void 0 : agencyContent.clickUrl) {
            handleResultClick("".concat(agencyContent.clickUrl, "?experience=v2"), true);
        }
    }
    useEffectOnceWhen(function () {
        impliedSawSavings(__assign(__assign({}, commonAnalyticsProperties), { impressionType: impressionType, impressionId: impressionId, userState: userState === null || userState === void 0 ? void 0 : userState.abbreviation }));
    }, Boolean(impressionId));
    return (React.createElement(React.Fragment, null,
        React.createElement(Header, { showDropShadow: true },
            React.createElement(HeaderLogo, { logo: "/assets/images/savvy-full-logo.svg" })),
        React.createElement(AppBody, { className: "px-6 pt-8" },
            React.createElement("div", { className: "relative mb-6 flex h-[160px] items-center justify-center overflow-hidden rounded-[20px] bg-green text-center" },
                React.createElement("div", { className: "absolute right-[-22px] top-[-21px]" },
                    React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 24 24", fill: "currentColor", className: "pointer-events-none h-[100px] w-[100px] text-[#6CB749]" },
                        React.createElement("path", { d: "M10.464 8.746c.227-.18.497-.311.786-.394v2.795a2.252 2.252 0 01-.786-.393c-.394-.313-.546-.681-.546-1.004 0-.323.152-.691.546-1.004zM12.75 15.662v-2.824c.347.085.664.228.921.421.427.32.579.686.579.991 0 .305-.152.671-.579.991a2.534 2.534 0 01-.921.42z" }),
                        React.createElement("path", { fillRule: "evenodd", d: "M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v.816a3.836 3.836 0 00-1.72.756c-.712.566-1.112 1.35-1.112 2.178 0 .829.4 1.612 1.113 2.178.502.4 1.102.647 1.719.756v2.978a2.536 2.536 0 01-.921-.421l-.879-.66a.75.75 0 00-.9 1.2l.879.66c.533.4 1.169.645 1.821.75V18a.75.75 0 001.5 0v-.81a4.124 4.124 0 001.821-.749c.745-.559 1.179-1.344 1.179-2.191 0-.847-.434-1.632-1.179-2.191a4.122 4.122 0 00-1.821-.75V8.354c.29.082.559.213.786.393l.415.33a.75.75 0 00.933-1.175l-.415-.33a3.836 3.836 0 00-1.719-.755V6z", clipRule: "evenodd" }))),
                React.createElement("div", { className: "relative text-white" },
                    React.createElement("p", { className: "mb-2.5 text-sm" }, "We estimate we can save you"),
                    React.createElement("p", { className: "mb-2.5 text-[32px] font-semibold" },
                        formatDollars(convertCentsToDollars(annualSavings)),
                        "/year"),
                    React.createElement("p", { className: "text-sm" }, "by shopping for insurance on your behalf"))),
            React.createElement("div", { className: "mb-6 flex items-center justify-center pl-1" },
                issuers.map(function (issuer, index) { return (React.createElement("div", { className: "relative -ml-1 h-10 w-10 rounded-full ring-2 ring-white", key: issuer.id, style: { zIndex: 10 - index } },
                    React.createElement(SavvyIssuerIcon, { issuerId: issuer.id, insuranceType: insuranceProduct === InsuranceProducts.Auto
                            ? InsuranceType.Auto
                            : insuranceProduct === InsuranceProducts.Home
                                ? InsuranceType.Home
                                : undefined }))); }),
                React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 40 40", className: "h-10 w-10" },
                    React.createElement("rect", { width: "40", height: "40", fill: "#F5F5F5", rx: "20" }),
                    React.createElement("path", { fill: "#999", fillRule: "evenodd", d: "M19.516 13.125c.345 0 .625.28.625.625v5.625h5.625a.625.625 0 1 1 0 1.25h-5.625v5.625a.625.625 0 1 1-1.25 0v-5.625h-5.625a.625.625 0 1 1 0-1.25h5.625V13.75c0-.345.28-.625.625-.625Z", clipRule: "evenodd" }))),
            React.createElement("div", { className: "mb-6 space-y-3 px-6 text-center" },
                (stateData === null || stateData === void 0 ? void 0 : stateData.num_of_insurers) ? (stateData.num_of_insurers > 6 ? (React.createElement("p", null,
                    "We will confirm quotes with up to",
                    " ",
                    React.createElement("strong", null,
                        stateData.num_of_insurers,
                        " insurers"),
                    " in",
                    " ", userState === null || userState === void 0 ? void 0 :
                    userState.name,
                    " for you.")) : (React.createElement("p", null,
                    "We will confirm quotes with ",
                    React.createElement("strong", null, "top insurers"),
                    " in",
                    " ", userState === null || userState === void 0 ? void 0 :
                    userState.name,
                    " for you."))) : null,
                React.createElement("p", null, "We just need a little more information to get you accurate quotes.")),
            React.createElement(Button, { onClick: handleClick }, "Continue"),
            footerContent ? (React.createElement(FooterContent, null,
                "Insurance Quotes Provided by:",
                React.createElement("br", null),
                footerContent.agency,
                React.createElement("br", null),
                footerContent.licenseText)) : null)));
}
